import {Component, OnInit, Input} from '@angular/core';
import {LineChartItem, CurrentValuesData, LineChartStatistics, Point} from "./line-chart-models";
import {KlaHttpService} from "../../tools/services/kla.http.service";
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kla-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
  standalone: true,
  imports: [ CommonModule ]
})
export class LineChartComponent implements OnInit {

  @Input()
  public title: string;

  @Input()
  public value: any;

  @Input()
  public image: string;

  @Input()
  public descriptions: Array<string>;

  @Input()
  public width: number = 800;

  @Input()
  public height: number = 500;

  @Input()
  public padding: number = 80;

  @Input()
  public currentValues: Array<LineChartItem> = [];

  @Input()
  public plannedValues: Array<Array<number>> = [];

  public currentData: CurrentValuesData;
  public plannedData: Array<string>;
  public imageGroupPosition: string = "left";
  public valueWidth: number = 0;
  public titleWidth: number = 0;
  public imageWidth: number = 100;
  public preparedTitle: string;
  public preparedValue: number;
  public preparedCenter: Point;
  public imagePath: string;
  public animationPrefixLength: number = 0;
  public animationPrefix: string = "";
  public animationSuffix: string = "";
  public generatedCharacter: string = "";
  public animationFinished = false;
  public animationExecuted = false;
  public intervalInstance: any;
  public initialValue: number;
  public transformImage = 'translate(0 0)';
  public fontSize: string;

  constructor(private httpService: KlaHttpService) { }

  ngOnInit() {
    this.imagePath = this.httpService.getImagePath(this.image, 500);
    this.preparedTitle = this.title;
    this.preparedValue = this.value;

    if (this.height < 400) {
      this.height = 400;
    }
    if (this.width > 620) {
      this.fontSize = '12px';
    } else if (this.width < 500) {
      this.fontSize = '9px';
    }

    let statistics: LineChartStatistics = this.calculateLineChartStatistics();
    this.preparePlanedData(statistics);
    this.prepareCurrentData(statistics);
    this.calculateImageGroupData();
    this.animationExecuted = true;
    this.animationFinished = true;
  }

  @Input('width')
  set setWidth(width: number) {
    this.plannedData = [];
    this.width = width;
    this.ngOnInit();
  }

  public selectPoint(point: Point) {
    if (!this.initialValue) {
      this.initialValue = this.value;
    }
    this.currentValues.forEach(item => {
      if (item.id == point.id) {
        this.value = item.value;
        item.center = true;
      } else {
        item.center = false;
      }
    });
    this.ngOnInit();
  }

  public mouseOverImageGroup() {

    //this.generatedCharacter = this.randomCharacter();
    if (!this.animationExecuted) {
      this.animationExecuted = true;
      this.animationFinished = false;
      this.animateValue(0);
      this.intervalInstance = setInterval(()=> {
        this.generatedCharacter = this.randomCharacter();
      }, 50);
    }
  }

  public mouseLeaveImageGroup() {
    this.animationExecuted = false;
  }

  public randomCharacter() {
    let possible = "0123456789";
    return possible.charAt(Math.floor(Math.random() * possible.length));
  }

  private animateValue(index: number) {
    if (this.preparedValue && (this.preparedValue.toString().length - 1 > index)) {
      this.animationPrefix = this.preparedValue.toString().substring(0, index);
      this.animationSuffix = this.preparedValue.toString().substring(index, index + 1);
      this.animationPrefixLength = this.calculateTextWidth(this.animationPrefix, 60, "Arial");
      setTimeout(() => {
        this.animateValue(index+1);
      }, 300);
    } else {
      this.animationPrefix = "";
      this.animationSuffix = "";
      this.animationFinished = true;
      clearInterval(this.intervalInstance);
    }
  }

  private calculateLineChartStatistics(): LineChartStatistics {
    let statistics: LineChartStatistics = {
      maxValue: -99999999,
      minValue: 99999999
    };
    this.plannedValues.forEach((planedLine: Array<number>) => {
      planedLine.forEach((value: number) => {
        value = value ? value : 0;
        statistics.maxValue = !statistics.maxValue ? value: (value > statistics.maxValue ? value : statistics.maxValue);
        statistics.minValue = !statistics.minValue ? value: (value < statistics.minValue ? value : statistics.minValue);
      });
    });
    this.currentValues.forEach((item: LineChartItem, index: number) => {
      item.id = index;
      let value = item.value;
      item.value = item.value ? item.value : 0;
      statistics.maxValue = !statistics.maxValue ? value: (value > statistics.maxValue ? value : statistics.maxValue);
      statistics.minValue = !statistics.minValue ? value: (value < statistics.minValue ? value : statistics.minValue);
    });
    return statistics;
  }

  private preparePlanedData(statistics: LineChartStatistics) {
    if (this.plannedValues && this.plannedValues.length > 0) {
      let maxValue = statistics.maxValue, minValue = statistics.minValue, difference, heightUnit, type;
      if (maxValue >= 0 && minValue >= 0) { type = "pp" }
      if (maxValue >= 0 && minValue <= 0) { type = "pn" }
      if (maxValue <= 0 && minValue <= 0) { type = "nn" }
      difference = maxValue - minValue;
      if (difference != 0) {
        heightUnit = (this.height - (2*this.padding))/difference;
      } else {
        heightUnit = (this.height - (2*this.padding))/(maxValue == 0 ? 2 : maxValue);
      }
      this.createPanedData(maxValue, minValue, heightUnit, type);
    }
  }

  private createPanedData(maxValue:number, minValue:number, heightUnit:number, type:string) {
    if (!this.plannedData) {
      this.plannedData = [];
    }
    this.plannedValues.forEach((items: Array<number>) => {
      let widthUnit = this.width/(items.length - 1);
      let points: Array<Point> = [];
      items.forEach((value: number, index: number) => {
        let point: Point = {
          x: (index * widthUnit),
          y: this.calculateY(value, maxValue, minValue, heightUnit, type)
        };
        points.push(point);
      });
      this.createPathThroughPoints(points, this.plannedData);
    });
  }

  private prepareCurrentData(statistics: LineChartStatistics) {
    if (this.currentValues && this.currentValues.length > 0) {
      let maxValue = statistics.maxValue, minValue = statistics.minValue, difference, widthUnit, heightUnit, type;
      if (maxValue >= 0 && minValue >= 0) { type = "pp" }
      if (maxValue >= 0 && minValue <= 0) { type = "pn" }
      if (maxValue <= 0 && minValue <= 0) { type = "nn" }
      difference = maxValue - minValue;
      widthUnit = (this.width - (2*this.padding))/(this.currentValues.length - 1);
      if (difference != 0) {
        heightUnit = (this.height - (2*this.padding))/difference;
      } else {
        heightUnit = (this.height - (2*this.padding))/(maxValue == 0 ? 2 : maxValue);
      }
      this.createCurrentData(maxValue, minValue, widthUnit, heightUnit, type);
    }
  }

  private calculateImageGroupData(): void {
    this.valueWidth = this.calculateTextWidth(this.value.toString(), 60, "Arial");
    this.titleWidth = this.calculateTitleWidth();
    if (this.preparedCenter) {
      let center = this.preparedCenter;
      let centerPosition = this.calculateCenterPosition();
      if (centerPosition == 2) {
        if (center.x >= center.y) {
          this.imageGroupPosition = 'bottom';
        } else {
          this.imageGroupPosition = 'right';
        }
      }
      if (centerPosition == 4) {
        if (center.x >= center.y) {
          this.imageGroupPosition = 'top';
        } else {
          this.imageGroupPosition = 'left';
        }
      }
      if (centerPosition == 1) {
        if ((center.x - this.width/2) >= center.y) {
          this.imageGroupPosition = 'left';
        } else {
          this.imageGroupPosition = 'bottom';
        }
      }
      if (centerPosition == 3) {
        if (center.x >= (center.y - this.height/2)) {
          this.imageGroupPosition = 'top';
        } else {
          this.imageGroupPosition = 'right';
        }
      }
      let topCoefficient = this.imageWidth + 20;
      let leftCoefficient = this.imageWidth + 20;
      let rightCoefficient = this.titleWidth + this.valueWidth + 20;
      let bottomCoefficient = this.imageWidth + 20;
      if (center.x > leftCoefficient && center.x < this.width - rightCoefficient) {
        if (center.y < topCoefficient) {
          this.imageGroupPosition = 'bottom';
        }
        if (center.y > this.height - bottomCoefficient) {
          this.imageGroupPosition = 'top';
        }
      }
      if (center.x < leftCoefficient && center.y < topCoefficient) {
        this.imageGroupPosition = 'bottom';
        this.transformImage = 'translate(' + leftCoefficient + ' 0)';
      } else if (center.x < leftCoefficient && center.y > this.height - bottomCoefficient) {
        this.imageGroupPosition = 'top';
        this.transformImage = 'translate(' + leftCoefficient + ' 0)';
      } else if (center.x > this.width - rightCoefficient && center.y < topCoefficient) {
        this.imageGroupPosition = 'bottom';
        this.transformImage = 'translate(' + (-rightCoefficient) + ' 0)';
      } else if (center.x > this.width - rightCoefficient && center.y > this.height - bottomCoefficient) {
        this.imageGroupPosition = 'top';
        this.transformImage = 'translate(' + (-rightCoefficient) + ' 0)';
      } else {
        this.transformImage = 'translate(0 0)';
      }
      let legendWidth = (this.valueWidth + this.titleWidth + this.imageWidth)*2;
      if (legendWidth > this.width) {
        this.setWidth = legendWidth;
      }
    }
  }

  private calculateCenterPosition(): number {
    let center = this.preparedCenter;
    let width = this.width;
    let height = this.height;
    if (center.x >= width/2) {
      if (center.y >= height/2) {
        return 4;
      } else {
        return 1;
      }
    } else {
      if (center.y >= height/2) {
        return 3;
      } else {
        return 2;
      }
    }
  }

  private createCurrentData(maxValue:number, minValue:number, widthUnit:number, heightUnit:number, type: string) {
    let data: CurrentValuesData = {annotations: [], lines: [], points: [], center: { x: null, y: null}};
    this.insertCurrentData(data, maxValue, minValue, widthUnit, heightUnit, type);
    this.currentData = data;
  }

  private insertCurrentData(data: any, maxValue:number, minValue:number, widthUnit:number, heightUnit:number, type: string) {
    this.currentValues.forEach((item: LineChartItem, index: number) => {
      let point: Point = {
        x:  this.padding + (index * widthUnit),
        y: this.calculateY(item.value, maxValue, minValue, heightUnit, type),
        id: item.id
      };
      if (point.y) {
        if (index == 0) { data.points.push({x: -this.padding, y:point.y}); }
        data.points.push(point);
        if (index == this.currentValues.length - 1) { data.points.push({x: point.x + this.padding*2, y:point.y}); }
        this.insertCurrentAnnotation(data, point, item.annotation, item.center);
      }
      if (item.center) {
        data.center = point;
        this.preparedCenter = point;
      }
    });
    this.createPathThroughPoints(data.points, data.lines);
  }

  private insertCurrentAnnotation(data:any, point: Point, annotation:string, center: boolean) {
    annotation = annotation ? annotation : "";
    let smallValue = annotation;
    if (smallValue.length > 16) {
      smallValue = smallValue.substring(0, 16);
    }
    data.annotations.push({
      annotation: smallValue,
      fullAnnotation: annotation,
      x: point.x - (this.calculateTextWidth(smallValue, 12, "Arial")/2),
      y: point.y + this.padding/2 + 10 + (center ? 10 : 0)
    })
  }

  private createPathThroughPoints(points: Array<Point>, oldPathDefinitions?: Array<string>): Array<string> {
    let pathDefinitions = [];
    if (oldPathDefinitions) {
      pathDefinitions = oldPathDefinitions;
    }
    let cx = this.computeControlPoints(points.map(point => point.x));
    let cy = this.computeControlPoints(points.map(point => point.y));

    points.forEach((point: Point, i: number) => {
      if (i != points.length - 1) {
        let next = points[i + 1];
        let path = this.createPath(point.x, point.y, cx.p1[i], cy.p1[i], cx.p2[i], cy.p2[i], next.x, next.y);

        pathDefinitions.push(path);
      }
    });
    return pathDefinitions;
  }

  private calculateY(value: number, maxValue:number, minValue:number, heightUnit:number, type: string) {
    if (type == "pp") {
      return this.height - this.padding - ((value - minValue) * heightUnit);
    }
    if (type == "pn") {
      return this.height - this.padding - ((value - minValue) * heightUnit);
    }
    if (type == "nn") {
      return this.padding - ((value - maxValue) * heightUnit);
    }
  }

  //https://www.particleincell.com/wp-content/uploads/2012/06/bezier-spline.js
  private createPath(x1, y1, px1, py1, px2, py2, x2, y2) {
    return "M " + x1 + " " + y1 + " C " + px1 + " " + py1 + " " + px2 + " " + py2 + " " + x2 + " " + y2;
  }

  //https://www.particleincell.com/wp-content/uploads/2012/06/bezier-spline.js
  private computeControlPoints(coordinates: Array<number>) {
    let p1 = [], p2 = [], n = coordinates.length - 1, a = [], b = [], c = [], r = [];
    a[0] = 0;
    b[0] = 2;
    c[0] = 1;
    r[0] = coordinates[0] + 2 * coordinates[1];
    for (let i = 1; i < n - 1; i++) {
      a[i] = 1;
      b[i] = 4;
      c[i] = 1;
      r[i] = 4 * coordinates[i] + 2 * coordinates[i + 1];
    }
    a[n - 1] = 2;
    b[n - 1] = 7;
    c[n - 1] = 0;
    r[n - 1] = 8 * coordinates[n - 1] + coordinates[n];
    for (let i = 1; i < n; i++) {
      let m = a[i] / b[i - 1];
      b[i] = b[i] - m * c[i - 1];
      r[i] = r[i] - m * r[i - 1];
    }
    p1[n - 1] = r[n - 1] / b[n - 1];
    for (let i = n - 2; i >= 0; --i) p1[i] = (r[i] - c[i] * p1[i + 1]) / b[i];
    for (let i = 0; i < n - 1; i++) p2[i] = 2 * coordinates[i + 1] - p1[i + 1];
    p2[n - 1] = 0.5 * (coordinates[n] + p1[n - 1]);
    return {p1: p1, p2: p2};
  }

  private calculateTextWidth(text: string, fontSize: number, fontFamily: string): number {
    let canvas = document.createElement("canvas");
    let context = canvas.getContext("2d");
    context.font = fontSize + "px " + fontFamily;
    return context.measureText(text).width;
  }

  private calculateTitleWidth() {
    let max = this.calculateTextWidth(this.preparedTitle, 16, "Arial");
    this.descriptions.forEach(description => {
      let descSize = this.calculateTextWidth(description, 16, "Arial")
      if (descSize > max) {
        max = descSize;
      }
    });
    return max;
  }
}
